import 'projects/sharedComponents/src/pet/careResources/contentful/richTextDefaultOptions.scss';

import { BLOCKS, Block, INLINES, Inline } from '@contentful/rich-text-types';
import { Box, Typography } from '@mui/material';
import { ReactNode, useMemo } from 'react';

import { ContentfulAssetFragment } from 'projects/sharedComponents/src/generated/contentful-graphql-types';
import { ContentfulLink } from 'projects/sharedComponents/src/pet/careResources/contentful/links/contentfulLink';
import { Options as ContentfulRenderOptions } from '@contentful/rich-text-react-renderer';
import { PersonalizedVariableContentFragment } from 'projects/sharedComponents/src/generated/graphql-client-types';
import { replaceTextPersonalizedVariableContent } from 'projects/sharedComponents/src/pet/careResources/contentful/replacePersonalizedVariableContent';

const headerClassName = 'contentful-header';

export const richTextDefaultOptions = (pet: PersonalizedVariableContentFragment): ContentfulRenderOptions => {
  return {
    renderNode: {
      [BLOCKS.HEADING_1]: (_node, children) => (
        <Typography component="h1" variant="h4" className={headerClassName}>
          {children}
        </Typography>
      ),
      [BLOCKS.HEADING_2]: (_node, children) => (
        <Typography component="h2" variant="h5" className={headerClassName}>
          {children}
        </Typography>
      ),
      [BLOCKS.HEADING_3]: (_node, children) => (
        <Typography component="h3" variant="h6" className={headerClassName}>
          {children}
        </Typography>
      ),
      [BLOCKS.HEADING_4]: (_node, children) => (
        <Typography component="h4" variant="h6" className={headerClassName}>
          {children}
        </Typography>
      ),

      [BLOCKS.HEADING_5]: (_node, children) => (
        <Typography component="h5" variant="h6" className={headerClassName}>
          {children}
        </Typography>
      ),
      [BLOCKS.HEADING_6]: (_node, children) => (
        <Typography component="h6" variant="h6" className={headerClassName}>
          {children}
        </Typography>
      ),
      [BLOCKS.PARAGRAPH]: (_node, children) => (
        <Typography variant="body1" className="contentful-paragraph">
          {children}
        </Typography>
      ),
      [BLOCKS.OL_LIST]: (_node, children) => <ol className="contentful-ol-list">{children}</ol>,
      [BLOCKS.UL_LIST]: (_node, children) => <ul className="contentful-ul-list">{children}</ul>,
      [BLOCKS.LIST_ITEM]: (_node, children) => <li className="contentful-listItem">{children}</li>,
      [INLINES.HYPERLINK]: (node, children) => {
        return (
          <ContentfulLink pet={pet} href={node.data.uri} className="contentful-link">
            {children}
          </ContentfulLink>
        );
      },
      [BLOCKS.QUOTE]: (_node, children) => {
        return <Box className="contentful-quote">{children}</Box>;
      },
    },
    renderText: (text) => replaceTextPersonalizedVariableContent(pet, text),
  };
};

type LinkedContentType = {
  __typename: string;
  sys: {
    id: string;
  };
} | null;

type ContentFragmentType = {
  json: any;
  links: {
    entries: {
      hyperlink: LinkedContentType[];
    };
    assets: {
      block: LinkedContentType[];
    };
  };
};

// A mapping of each Content ID (e.x. a Care Resources Product ID from contentful) to the actual object from the GraphQL query.
type ContentfulIDToContentMap = Map<string, NonNullable<LinkedContentType>>;

/**
 * Returns a mapping of each product's ID to the GraphQL Product object.
 * When we use links to products in Contentful, they only send back the ID and we have to find the product in the response.
 *
 * For an example, see: https://www.contentful.com/blog/2021/04/14/rendering-linked-assets-entries-in-contentful/#:~:text=Rendering%20Rich%20Text%20references%20using%20the%20GraphQL%20API
 */
function getTypenameToContentMap(richText: ContentFragmentType): ContentfulIDToContentMap {
  const idToContent: ContentfulIDToContentMap = new Map();

  const allLinkedContent: LinkedContentType[] = [...richText.links.entries.hyperlink, ...richText.links.assets.block];

  for (const link of allLinkedContent) {
    if (!link) {
      continue;
    }

    idToContent.set(link.sys.id, link);
  }

  return idToContent;
}

type RichTextRenderOptions<T extends ContentFragmentType> = {
  /**
   * Required if using links to Content in rich text.
   *
   * You must use the `__typename` field to figure out what type of Content is linked and then render a component.
   *
   * Example:
   *
   * ```tsx
   * const articleBodyRichTextOptions = useContentfulRichTextOptions(article?.articleBody, {
   *   renderLink: (content, _node, children) => {
   *     if (content.__typename === 'CareResourcesProduct') {
   *       // The content field is now strongly typed. The children field is the link text.
   *       return <MyProductLink product={content}>{children}</MyProductLink>;
   *     }
   *   }
   * });
   * ```
   */
  renderLink?: (
    content: NonNullable<T['links']['entries']['hyperlink'][number]>,
    node: Block | Inline,
    children: ReactNode
  ) => ReactNode;
  /**
   * Optional, allows changing the default image rendering.
   * If not set, an <img> tag will be created that is 100% width.
   */
  renderImage?: (
    content: NonNullable<T['links']['assets']['block'][number]>,
    node: Block | Inline,
    children: ReactNode
  ) => ReactNode;
};

/**
 * Simplifies the Contentful rendering of rich text by automatically building a map of ID to Content.
 *
 * To use this, pass in the rich text object and then pass in a function to convert the Content into React.
 *
 * This is all strongly typed with TypeScript, simply use the __typename field to determine what type of Content Contentful returned.
 *
 * Usage:
 *
 * To turn a specific model into a link:
 *
 * ```tsx
 *   const articleBodyRichTextOptions = useContentfulRichTextOptions(article?.articleBody, {
 *   renderLink: (content, _node, children) => {
 *     if (content.__typename === 'CareResourcesProduct') {
 *       // The content field is now strongly typed. The children field is the link text.
 *       return <MyProductLink product={content}>{children}</MyProductLink>;
 *     }
 *
 *     return null;
 *   },
 * });
 * ```
 */
export function useContentfulRichTextOptions<T extends ContentFragmentType>(
  pet: PersonalizedVariableContentFragment,
  richText: T | null | undefined,
  renderOptions: RichTextRenderOptions<T>
): ContentfulRenderOptions {
  const idToContentMap = useMemo<ContentfulIDToContentMap>(() => {
    if (!richText) {
      return new Map();
    }

    return getTypenameToContentMap(richText);
  }, [richText]);

  return {
    renderNode: {
      ...richTextDefaultOptions(pet).renderNode,
      [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
        // Find the actual product from the map of ID to product.
        const content = findContentInMap(idToContentMap, node) as ContentfulAssetFragment | null;

        if (!content) {
          return <></>;
        }

        if (!content.contentType?.startsWith('image')) {
          console.error('Unrecognized content type: ' + JSON.stringify(content, undefined, 2));
          return <></>;
        }

        // Should never happen as this is automatically set by Contentful.
        if (!content.url) {
          return <></>;
        }

        if (!!renderOptions.renderImage) {
          return renderOptions.renderImage(
            content as unknown as NonNullable<T['links']['assets']['block'][number]>,
            node,
            children
          );
        }

        return (
          <img
            className="contentful-richtext-image"
            src={content.url}
            alt={`${content.title} ${content.description}`}
          />
        );
      },
      // To support inline or embedded links to Contentful Content Models, you MUST add your own code that supports that specific Content model.
      // Without this you get a blob of nonsense text in the article.
      // See: https://www.contentful.com/blog/2021/04/14/rendering-linked-assets-entries-in-contentful/#:~:text=Rendering%20Rich%20Text%20references%20using%20the%20GraphQL%20API
      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        if (!renderOptions.renderLink) {
          console.error(
            'Contentful hyperlink entry was detected in the content. Please define a renderLink option in useContentfulRichTextOptions'
          );
          return children;
        }

        // Find the actual product from the map of ID to product.
        const content = findContentInMap(idToContentMap, node);

        if (!content) {
          return <></>;
        }

        return renderOptions.renderLink(
          content as unknown as NonNullable<T['links']['entries']['hyperlink'][number]>,
          node,
          children
        );
      },
    },
  };
}

function findContentInMap(idToContentMap: ContentfulIDToContentMap, node: Block | Inline): LinkedContentType {
  // In the rich text response, get the ID of the product we need to lookup.
  const contentID: string | undefined = node.data.target?.sys?.id;

  // I have no idea if this is possible or not since `node.data` is weakly typed.
  // To be safe, bail out if we do not get an ID.
  if (!contentID) {
    return null;
  }

  // Find the actual product from the map of ID to product.
  const content = idToContentMap?.get(contentID);

  // This should never happen, but we recover if it does.
  if (!content) {
    console.error(`No Contentful Content found with id = ${contentID}`);
    return null;
  }

  return content;
}
